import { cn } from "@/lib/utils";
import { useEffect, useId, useState } from "react";
import Description from "../description";
import FieldError from "../field-error";
import Input from "../input";
import Label from "../label";

export interface RangeTextProps {
	label?: string;
	description?: string | null;
	error?: string;
	containerClassName?: string;
	required?: boolean;
	id?: string;
	name?: string;
	mode?: "single" | "range" | null;
	value?: number | number[];
	defaultValue?: number | number[];
	onValueChange?: (value: number | number[]) => void;
	onBlur?: () => void;
	min?: number;
	max?: number;
	placeholder?: string | null;
	minPlaceholder?: string | null;
	maxPlaceholder?: string | null;
}

function formatBoundValue(value: number | undefined, bound: number): string {
	if (value === undefined || value === bound) return "";
	return value.toString();
}

function parseBoundValue(raw: string, bound: number): number {
	if (raw === "") return bound;

	const parsed = Number(raw);
	return Number.isNaN(parsed) ? bound : parsed;
}

const RangeText: React.FC<RangeTextProps> = ({
	label,
	description,
	error,
	containerClassName,
	required,
	mode = "range",
	value: valueProp,
	defaultValue: defaultValueProp,
	onValueChange,
	onBlur,
	min = 0,
	max = 100,
	placeholder,
	minPlaceholder = "Min",
	maxPlaceholder = "Max",
	name,
	...props
}) => {
	const generatedId = useId();
	const id = props.id ?? generatedId;
	const isRange = mode === "range";
	const currentValue = valueProp ?? defaultValueProp ?? (isRange ? [min, max] : min);
	const values = Array.isArray(currentValue) ? currentValue : [currentValue];
	const [minInput, setMinInput] = useState(() => formatBoundValue(values[0], min));
	const [maxInput, setMaxInput] = useState(() => formatBoundValue(values[1], max));
	const [singleInput, setSingleInput] = useState(() => formatBoundValue(values[0], min));

	useEffect(() => {
		const nextValues = Array.isArray(currentValue) ? currentValue : [currentValue];

		if (isRange) {
			setMinInput(formatBoundValue(nextValues[0], min));
			setMaxInput(formatBoundValue(nextValues[1], max));
			return;
		}

		setSingleInput(formatBoundValue(nextValues[0], min));
	}, [currentValue, isRange, min, max]);

	const commitRange = (nextMin: number, nextMax: number) => {
		onValueChange?.([Math.min(nextMin, nextMax), Math.max(nextMin, nextMax)]);
	};

	const commitSingle = (nextValue: number) => {
		onValueChange?.(nextValue);
	};

	if (isRange) {
		return (
			<div className={cn("flex flex-col items-start gap-2", containerClassName)}>
				{label && (
					<Label variant="native" htmlFor={id} required={required}>
						{label}
					</Label>
				)}

				<div className="flex w-full items-center gap-2">
					<Input
						type="number"
						hideLabel
						id={`${id}-min`}
						name={name ? `${name}-min` : undefined}
						placeholder={minPlaceholder ?? undefined}
						value={minInput}
						min={min}
						max={max}
						onChange={(event) => setMinInput(event.target.value)}
						onBlur={() => {
							onBlur?.();
							commitRange(parseBoundValue(minInput, min), parseBoundValue(maxInput, max));
						}}
						containerClassName="flex-1 min-w-0"
					/>

					<span className="text-(--ms-form-el-color)">-</span>

					<Input
						type="number"
						hideLabel
						id={`${id}-max`}
						name={name ? `${name}-max` : undefined}
						placeholder={maxPlaceholder ?? undefined}
						value={maxInput}
						min={min}
						max={max}
						onChange={(event) => setMaxInput(event.target.value)}
						onBlur={() => {
							onBlur?.();
							commitRange(parseBoundValue(minInput, min), parseBoundValue(maxInput, max));
						}}
						containerClassName="flex-1 min-w-0"
					/>
				</div>

				{description && <Description>{description}</Description>}
				<FieldError message={error} />
			</div>
		);
	}

	return (
		<Input
			type="number"
			id={id}
			name={name}
			label={label}
			required={required}
			placeholder={placeholder ?? undefined}
			description={description}
			error={error}
			containerClassName={containerClassName}
			value={singleInput}
			min={min}
			max={max}
			onChange={(event) => setSingleInput(event.target.value)}
			onBlur={() => {
				onBlur?.();
				commitSingle(parseBoundValue(singleInput, min));
			}}
		/>
	);
};

export default RangeText;
